import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useCreateOnlineMeetingActions from './CreateOnlineMeeting.actions.hook';
import { onlineMeetingTransformer } from './CreateOnlineMeeting.transformers';
import {
  CreateOnlineMeetingStepsIndex,
  CreateOnlineMeetingWithEmailConfigurationStepsIndex,
} from './CreateOnlineMeeting.types';
import useCreateOnlineMeetingButtons from './CreateOnlineMeetingButtons.hook';
import useCreateOnlineMeetingStepper from './CreateOnlineMeetingStepper.hook';
import { eventsRoute } from 'basics/constants/routes.constants';
import { warningToast } from 'basics/utils/toast';
import { EventStatus, Presence } from 'generated/event.types';
import { isDraft } from 'modules/events/basics/utils/events.utils';
import { EditEventButtonActionStates } from 'modules/events/components/EditEventButtonsBar/EditEventButtonsBar.types';
import useLazyGetFullPrivateEventById from 'modules/events/graphql/queries/getLazyFullPrivateEventById';
import eventState from 'modules/events/states/events.states';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCreateOnlineMeeting = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const flags = useFlags();
  const yodaFormMethods = useYodaCreateForm({ setIsValidOptional: true });
  const onlineMeetingActions = useCreateOnlineMeetingActions(yodaFormMethods);
  const { queryGetFullPrivateEventById, data: event, error, loading } = useLazyGetFullPrivateEventById();

  if (event && event.presence !== Presence.online) {
    warningToast(t('errors_standard'));
    navigate(eventsRoute);
  }

  const buttonsConfig = useCreateOnlineMeetingButtons(
    onlineMeetingActions,
    !isDraft(event?.status),
  );
  const stepsList = useCreateOnlineMeetingStepper(yodaFormMethods.useWatchStep);

  const isLastStep = flags.eventsBiogenlinc4151SectionEmail
    ? onlineMeetingActions.stepIndexer.stepIndex === CreateOnlineMeetingWithEmailConfigurationStepsIndex.publicationStep
    : onlineMeetingActions.stepIndexer.stepIndex === CreateOnlineMeetingStepsIndex.publicationStep;

  const actionStates: EditEventButtonActionStates = {
    updateLoading: onlineMeetingActions.updateLoading,
    publishLoading: onlineMeetingActions.publishLoading,
    readyToPublish: yodaFormMethods.formState.isValid && event?.status === EventStatus.ready,
    isLastStep,
  };

  useEffect(() => {
    if (id) {
      queryGetFullPrivateEventById({ variables: { eventId: id } });
    }
  }, [id, queryGetFullPrivateEventById]);

  useEffect(() => {
    if (event) {
      eventState.createEvent = onlineMeetingTransformer(event);
    }
    if (error) {
      warningToast(t('errors_standard'));
      navigate(eventsRoute);
    }
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickHome = async () => {
    navigate(eventsRoute);
  };

  const showLoader = loading || !event;

  return {
    t,
    handleClickHome,
    handleChangeStep: onlineMeetingActions.handleChangeStep,
    showLoader,
    yodaFormMethods,
    stepIndexer: onlineMeetingActions.stepIndexer,
    id,
    buttonsConfig,
    actionStates,
    stepsList,
    event,
  };
};

export default useCreateOnlineMeeting;
